/**
 * The other half of the AI's attack verbs.
 *
 * `EnemyAiCtx.meleeHit` and `EnemyAiCtx.spawnProjectile` are optional by
 * design — the comment on the interface says the AI has to keep running in a
 * world where the combat workstream has not landed — but nothing ever supplied
 * them, so every enemy melee swing, lunge, beam, aura and projectile resolved
 * to nothing. Enemies telegraphed, committed, recovered, and never touched the
 * player.
 *
 * These are the handlers. They live here rather than in the game shell because
 * they are combat rules, and because they need to share the projectile pool and
 * the parry window with the player's side of the fight — two implementations of
 * "a bullet" would drift, and a parry that only worked against one of them
 * would be worse than no parry at all.
 */

import type { Rng } from '../../core/rng';
import { BodyFlag, Comp, DamageType, Team, World } from '../../entity/ecs';
import type { DamageTypeId } from '../../entity/ecs';
import type { CellWorld } from '../../sim/world';
import { compositionEffectiveness, environmentEffectiveness } from '../../combat/resolve';
import { applyStatus } from '../../combat/status';
import { Projectiles, fireFlat } from '../../combat/projectiles';
import { resolveIncomingBlow } from '../../combat/wielder';
import type { WielderCtx, WielderState } from '../../combat/wielder';
import { StatusKind } from '../../combat/types';
import { Anchor, AttackKind, ShaperOp, type AttackDef, type EnemyDef } from '../enemyData';
import type { EnemyAiCtx } from './behaviors';

export interface EnemyAttackDeps {
  world: World;
  cells: CellWorld;
  projectiles: Projectiles;
  rng: Rng;
  /** The one damage door. Enemy hits must not write hp directly either. */
  damageEntity(idx: number, amount: number, type: number, srcIdx: number): void;
  /** Player entity index, or -1. Read per call so a respawn is picked up. */
  playerIdx(): number;
  /** The player's hands, so an incoming blow can be parried (docs/04 §2). */
  wielder?: WielderState | null;
  wielderCtx?: WielderCtx | null;
  /**
   * Presentation: flash, shake, hit spark.
   *
   * `amount` is the damage *after* composition and environment effectiveness —
   * the number the target actually loses — so a consumer that scales anything
   * by force is scaling by the force that landed rather than by what was
   * authored. It went unread for as long as it existed (#141), which is why it
   * is said here: a flat presentation for a 6-damage skitter bite and a
   * 20-damage boss slam is a decision, and it should be one somebody made.
   */
  onHit?(target: number, srcIdx: number, amount: number): void;
  /**
   * A blow turned aside by an open parry window.
   *
   * No direction, deliberately — see `onBlocked`. A parry's presentation is not
   * a shove; the event happens *to the attacker*, which `srcIdx` already names.
   */
  onParried?(target: number, srcIdx: number): void;
  /**
   * A blow stopped by a *raised guard*, which is not the same event as a parry
   * and must not share its presentation: a parry is a 220 ms read that opens a
   * counter, a block is a held state that spends a resource (docs/04 §3.1).
   * Both used to arrive here as `onParried`, so the shield's whole identity was
   * invisible above combat.
   *
   * `dirX`/`dirY` are the unit direction the blow was travelling, and they are
   * here because a block *is* directional: the guard holds and the wielder is
   * shoved along it. They are not on `onHit` or `onParried`, because neither of
   * those presents a shove and an argument no consumer reads is precisely the
   * defect this hook was found by (#141, #138). The alternative — letting the
   * integrator re-derive the direction from the two entities' positions — is
   * the *selection and lookup disagree* shape that produced #92, #127, #137 and
   * #139: `strike()` is handed the attack's own direction and the positions are
   * a different question that usually agrees.
   */
  onBlocked?(target: number, srcIdx: number, dirX: number, dirY: number): void;
}

/**
 * Iframes an enemy hit grants, seconds. Keeps an aura from ticking per frame.
 *
 * **This is a first-class balance knob, not an implementation detail.** It is
 * the denominator of every enemy's damage-per-second: `contactDamage` divided
 * by this number is the rate at which standing inside an enemy costs you HP
 * (11.4 dps for a fen leech, 74.3 for the kiln saint), and it is the ceiling on
 * how fast a sustained attack can re-hit. It is also a *single per-target*
 * window, so simultaneous contacts share it rather than stacking — which is
 * what keeps a pack of twelve from being twelve times a single enemy, and what
 * makes the number load-bearing rather than incidental.
 *
 * Exported so a balance pass can state it instead of rediscovering it.
 */
export const ENEMY_IFRAMES = 0.35;
/**
 * Aura/beam contact is sustained, so it uses a shorter re-hit gate.
 *
 * It is also the *schedule*: `attackPulses()` (behaviors.ts) derives how many
 * times a sustained attack lands from `active / SUSTAINED_IFRAMES`, and the
 * pulses are spaced exactly this far apart. Deriving both from one constant is
 * what stops a beam from scheduling a pulse its own iframe window would eat.
 */
export const SUSTAINED_IFRAMES = 0.22;

/**
 * Half-extent of an attack's hitbox, in world units.
 *
 * `range` is documented on `AttackDef` as the distance at which the AI will
 * *start* this attack — an aggro trigger. Sizing the hitbox from it made every
 * hitbox as wide as the decision to swing, which is an attack with no dodge
 * distance: `reliquary_beam` (range 340) resolved as a 680-unit-wide box in one
 * frame. The extent comes from `radius`.
 *
 * `radius` is 0 on every melee, lunge and attach in the roster, because until
 * now nothing read it. The two fallbacks below are derivations, not new
 * authored numbers, for the same reason `spawnProjectile` derives flight time
 * from range and speed: a third field could disagree with the two that produce
 * it.
 *
 * Everything without an authored `radius` reaches **as far as it is big**. The
 * authored melee `range`s are already 3.0–5.7× the owner's `halfW`, so the body
 * was the real source all along; taking it directly keeps a bone titan's slam
 * bigger than a silt crawler's bite without authoring 41 new numbers.
 *
 * **A lunge used to be the exception and no longer is** (#49). Its extent was
 * `speed × active` — the whole swept path, 88–136 units — because `fireAttack`
 * resolved the blow once, at the instant of launch, so the only way to hit
 * anything the dash would eventually reach was to claim all of that ground in
 * the first frame. That is a hitbox standing in for a movement, and it bought
 * an instantaneous 136-unit box with nothing to dodge while still landing on
 * nobody, because the trigger `range` was further out than the path.
 *
 * A lunge now resolves along its path, once per tick of its active window
 * (`sweptHit`), so the ground it covers is the ground the body covers and the
 * per-tick box is simply the body. The union over the window is at least what
 * the old one-shot box claimed, arrives when the body does, and can hit
 * something that steps in mid-dash.
 */
function hitExtent(w: World, srcIdx: number, atk: AttackDef): number {
  if (atk.radius > 0) return atk.radius;
  return Math.max(6, Math.max(w.halfW[srcIdx], w.halfH[srcIdx]));
}

export function makeEnemyAttacks(
  deps: EnemyAttackDeps,
): Pick<EnemyAiCtx, 'meleeHit' | 'sweptHit' | 'terrainHit' | 'spawnProjectile' | 'contactHit'> {
  const w = deps.world;

  const api = {
    meleeHit(srcIdx: number, atk: AttackDef, def: EnemyDef, dirX: number, dirY: number): void {
      // A blow that does not travel is a sweep of length zero. One geometry,
      // so a lunge and a swing cannot drift apart.
      api.sweptHit(srcIdx, atk, def, dirX, dirY, w.x[srcIdx], w.y[srcIdx]);
    },

    /**
     * A blow resolved over the ground the body covered between `(fromX, fromY)`
     * and where it stands now.
     *
     * This is what a lunge is (#49): the dash is the attack, so the damage
     * follows the body instead of being claimed in one frame from the launch
     * point. Called once per tick of the active window, it costs the same box
     * test as a swing and cannot tunnel past a target at any `dt` — including
     * the inflated `stepDt` an enemy gets when `updateEnemies` is round-robining
     * a crowded room, where a 400-unit-per-second dash covers 49 units in one
     * step and a point sample would pass straight through an 11-unit body.
     */
    sweptHit(
      srcIdx: number,
      atk: AttackDef,
      def: EnemyDef,
      dirX: number,
      dirY: number,
      fromX: number,
      fromY: number,
    ): void {
      if (atk.damage <= 0) return;
      if (w.alive[srcIdx] !== 1) return;

      const extent = hitExtent(w, srcIdx, atk);
      const team = w.team[srcIdx];

      // A beam is a *ray*. It used to be resolved as a sphere centred on the
      // caster — `dirX/dirY` were accepted and then discarded — so the one
      // attack in the game whose whole identity is "it points at you" was the
      // one attack that could not miss. Aiming it is the other half of sizing
      // it: a 220-unit beam centred on the boss is a 440-unit arena wipe.
      if (atk.kind === AttackKind.BEAM) {
        beamHit(deps, srcIdx, atk, def, dirX, dirY, extent, team);
        return;
      }

      // An aura surrounds the body; everything else swings out in front of it.
      const aura = atk.kind === AttackKind.AURA;
      const half = aura ? extent : extent * 0.6;
      const lead = aura ? 0 : extent * 0.5;
      const ax = fromX + dirX * lead;
      const ay = fromY + dirY * lead;
      const bx = w.x[srcIdx] + dirX * lead;
      const by = w.y[srcIdx] + dirY * lead;
      // The union of the box at both ends of the travel, which for a swing
      // (`from` = here) is exactly the box it has always been.
      const cx = (ax + bx) * 0.5;
      const cy = (ay + by) * 0.5;
      const halfX = half + Math.abs(bx - ax) * 0.5;
      const halfY = half + Math.abs(by - ay) * 0.5;

      boxHit(deps, srcIdx, atk, def, dirX, dirY, cx, cy, halfX, halfY, aura, team);
    },

    /**
     * The damage of a terrain op, resolved **at the op** (#46).
     *
     * `fireAttack` writes the shaper's cells at the resolved anchor — the
     * target's feet, the ground under them, the ceiling above them — and used
     * to ask for the damage at the *caster*. While the box was `range`-derived
     * at 132–440 units wide both positions sat inside it; once #37 shrank it to
     * the radius the op writes with, a ceiling-anchored flood wrote its cells
     * and resolved its damage 200 units away, hurting nobody.
     *
     * The ruling, stated here because it is a combat-design call and not
     * plumbing: **a terrain attack's damage belongs to the op's footprint.** It
     * is dealt where the cells are written, over the same `radius` they are
     * written with — not at the caster, and not at both. An `Anchor.SELF` op
     * still damages around the caster, because that is where it writes; every
     * other anchor does not, because the caster is not the one doing anything
     * there. "Both" would mean a shaper that lays lava at your feet from 200
     * units away also singes itself, and would put a second, larger hitbox back
     * on the family whose hitbox was just made honest.
     *
     * The consequence worth naming: an op anchored at a ceiling well over the
     * target's head damages nobody at the aperture, because nobody is standing
     * in it. What hurts them is the lava that falls out of it, which the cell
     * sim already models and which is the whole identity of the family. The
     * authored `damage` is the pour, not the pool.
     */
    terrainHit(
      srcIdx: number,
      atk: AttackDef,
      def: EnemyDef,
      dirX: number,
      dirY: number,
      atX: number,
      atY: number,
    ): void {
      if (atk.damage <= 0) return;
      if (w.alive[srcIdx] !== 1) return;
      // Symmetric around the anchor, like an aura and unlike a swing: the op
      // writes a band or a disc centred on that point, and the damage area is
      // the area the op writes cells into.
      const extent = hitExtent(w, srcIdx, atk);
      boxHit(deps, srcIdx, atk, def, dirX, dirY, atX, atY, extent, extent, false, w.team[srcIdx]);
    },

    /**
     * The body itself as a weapon: `contactDamage` is authored on all 54 defs
     * and, before this, was read by nothing — the safest place in the room was
     * the inside of the thing trying to kill you.
     *
     * It routes through the same `strike()` every other enemy blow uses, so a
     * raised guard stops it, the 220 ms parry reads it, composition and
     * environment effectiveness scale it, poise breaks on it and a fire-bodied
     * enemy ignites you with it. A second damage path would have opted out of
     * all six silently.
     */
    contactHit(srcIdx: number, def: EnemyDef): void {
      if (def.contactDamage <= 0) return;
      if (w.alive[srcIdx] !== 1 || w.hp[srcIdx] <= 0) return;

      const atk = contactAttack(def);
      const team = w.team[srcIdx];
      const sx = w.x[srcIdx];
      const sy = w.y[srcIdx];

      w.each(Comp.HEALTH, (j) => {
        if (j === srcIdx || w.hp[j] <= 0) return;
        if (w.team[j] === team || w.team[j] === Team.NEUTRAL) return;
        // The shared per-target window (see ENEMY_IFRAMES): overlapping three
        // enemies at once costs what overlapping one costs, and a weapon hit
        // and a body hit cannot both land in the same 0.35 s.
        if (w.iframes[j] > 0) return;
        const dx = w.x[j] - sx;
        const dy = w.y[j] - sy;
        if (Math.abs(dx) > w.halfW[srcIdx] + w.halfW[j]) return;
        if (Math.abs(dy) > w.halfH[srcIdx] + w.halfH[j]) return;
        // Knockback points out of the body, so contact pushes you off rather
        // than holding you inside the damage.
        const len = Math.hypot(dx, dy);
        const nx = len > 1e-4 ? dx / len : 1;
        const ny = len > 1e-4 ? dy / len : 0;
        strike(deps, srcIdx, j, atk, def, nx, ny, false);
      });
    },

    spawnProjectile(
      srcIdx: number,
      x: number,
      y: number,
      dirX: number,
      dirY: number,
      atk: AttackDef,
      def: EnemyDef,
    ): void {
      void def;
      if (w.alive[srcIdx] !== 1) return;
      // Muzzle offset keeps a round from spawning inside the shooter's own
      // body, where it would collide with the terrain the body is standing in.
      const off = Math.max(w.halfW[srcIdx], w.halfH[srcIdx]) + 2;
      fireFlat(
        deps.projectiles,
        w,
        srcIdx,
        x + dirX * off,
        y + dirY * off,
        dirX,
        dirY,
        atk.speed > 0 ? atk.speed : 300,
        atk.damage,
        atk.damageType,
        // Range and speed imply flight time; authoring a third number would let
        // the three disagree.
        Math.max(0.4, atk.range / Math.max(60, atk.speed)) + 0.6,
        atk.kind === AttackKind.PROJECTILE ? 0.25 : 0,
        3,
      );
    },
  };

  return api;
}

/**
 * The one box test every non-beam enemy blow resolves through.
 *
 * Centre and half-extents rather than "the attacker plus a shape", because the
 * three callers disagree about where the shape goes and agree about everything
 * else: a swing is in front of the body, an aura is around it, a terrain op is
 * wherever its anchor landed, and a lunge is smeared along the ground the body
 * just crossed. One selector, one iframe gate, one door into `strike()`.
 */
function boxHit(
  deps: EnemyAttackDeps,
  srcIdx: number,
  atk: AttackDef,
  def: EnemyDef,
  dirX: number,
  dirY: number,
  cx: number,
  cy: number,
  halfX: number,
  halfY: number,
  sustained: boolean,
  team: number,
): void {
  const w = deps.world;
  w.each(Comp.HEALTH, (j) => {
    if (j === srcIdx || w.hp[j] <= 0) return;
    if (w.team[j] === team || w.team[j] === Team.NEUTRAL) return;
    if (w.iframes[j] > 0) return;
    if (Math.abs(w.x[j] - cx) > halfX + w.halfW[j]) return;
    if (Math.abs(w.y[j] - cy) > halfY + w.halfH[j]) return;
    strike(deps, srcIdx, j, atk, def, dirX, dirY, sustained);
  });
}

/**
 * A beam, as a segment rather than a sphere: `length` along `(dirX, dirY)` from
 * the caster, and as wide as the body that emits it.
 *
 * The width is derived from `halfW` rather than authored so `radius` keeps one
 * meaning — *how far the attack reaches* — for every attack kind. A second
 * width field would be a second thing to get wrong, and there is nothing in the
 * roster that would distinguish it from the aperture it comes out of.
 */
function beamHit(
  deps: EnemyAttackDeps,
  srcIdx: number,
  atk: AttackDef,
  def: EnemyDef,
  dirX: number,
  dirY: number,
  length: number,
  team: number,
): void {
  const w = deps.world;
  // Normalise here: a caller that passed an unnormalised aim would otherwise
  // get a beam whose reach depended on which way it was pointing.
  const m = Math.hypot(dirX, dirY);
  const ux = m > 1e-6 ? dirX / m : 1;
  const uy = m > 1e-6 ? dirY / m : 0;
  const halfWidth = Math.max(6, w.halfW[srcIdx]);
  const sx = w.x[srcIdx];
  const sy = w.y[srcIdx];

  w.each(Comp.HEALTH, (j) => {
    if (j === srcIdx || w.hp[j] <= 0) return;
    if (w.team[j] === team || w.team[j] === Team.NEUTRAL) return;
    if (w.iframes[j] > 0) return;
    const rx = w.x[j] - sx;
    const ry = w.y[j] - sy;
    const pad = Math.max(w.halfW[j], w.halfH[j]);
    const along = rx * ux + ry * uy;
    if (along < -pad || along > length + pad) return;
    const across = Math.abs(ry * ux - rx * uy);
    if (across > halfWidth + pad) return;
    strike(deps, srcIdx, j, atk, def, ux, uy, true);
  });
}

/**
 * The synthetic `AttackDef` a body's own `contactDamage` resolves as.
 *
 * Built once per def and cached: `strike()` reads `damage`, `damageType` and
 * `kind`, and reaching it with a real AttackDef is what puts contact through
 * the same door as a swing instead of beside it.
 */
const contactDefs = new Map<string, AttackDef>();
function contactAttack(def: EnemyDef): AttackDef {
  let a = contactDefs.get(def.id);
  if (a === undefined) {
    a = {
      id: `${def.id}.contact`,
      kind: AttackKind.MELEE,
      damage: def.contactDamage,
      damageType: def.contactType,
      range: 0,
      windup: 0,
      active: 0,
      recover: 0,
      cooldown: 0,
      speed: 0,
      op: ShaperOp.FLOOD,
      mat: null,
      radius: 0,
      cells: 0,
      anchor: Anchor.SELF,
      pulses: 1,
      summon: null,
      count: 0,
    };
    contactDefs.set(def.id, a);
  }
  return a;
}

function strike(
  deps: EnemyAttackDeps,
  srcIdx: number,
  target: number,
  atk: AttackDef,
  def: EnemyDef,
  dirX: number,
  dirY: number,
  sustained: boolean,
): void {
  const w = deps.world;

  // Defence first: a stopped blow deals nothing, and a parry's counter has to
  // start inside the same frame or "parry into counter" is two inputs.
  if (target === deps.playerIdx() && deps.wielder && deps.wielderCtx) {
    const outcome = resolveIncomingBlow(deps.wielder, w, target, srcIdx, deps.wielderCtx);
    if (outcome === 'parried') {
      deps.onParried?.(target, srcIdx);
      return;
    }
    if (outcome === 'blocked') {
      deps.onBlocked?.(target, srcIdx, dirX, dirY);
      return;
    }
  }

  const type = atk.damageType as DamageTypeId;
  // Same derivation the player's weapons use: what the target is made of, and
  // what it is standing in. An enemy attack is not a special case (docs/03 §6).
  const eff =
    compositionEffectiveness(w, target, type) *
    environmentEffectiveness(w, deps.cells, target, type);
  const amount = atk.damage * eff;

  deps.damageEntity(target, amount, type, srcIdx);

  // A sustained hit is a field you are standing in, and it lands again every
  // SUSTAINED_IFRAMES. Punting the target once per pulse would both fight the
  // attack (knockback pushes you out of the thing whose point is that you are
  // inside it) and, with the vertical pop, take the fight off the ground and
  // keep it there. The pressure is the damage; the impulse belongs to blows.
  const kb = sustained ? 0 : (atk.kind === AttackKind.LUNGE ? 130 : 70) / Math.max(0.25, w.mass[target]);
  if (kb > 0 && (w.bodyFlags[target] & BodyFlag.NO_GRAVITY) === 0) {
    w.vx[target] += dirX * kb;
    w.vy[target] += dirY * kb * 0.4 - 24;
  }
  w.iframes[target] = Math.max(w.iframes[target], sustained ? SUSTAINED_IFRAMES : ENEMY_IFRAMES);

  // Poise loss and stagger use the same numbers the player's hits do, so being
  // hit reads the same in both directions.
  w.poise[target] -= atk.damage * 0.05;
  if (w.poise[target] <= 0 && w.maxPoise[target] > 0) {
    w.poise[target] = w.maxPoise[target];
    const dur = 0.2 + Math.min(0.5, atk.damage * 0.01);
    w.stagger[target] = Math.max(w.stagger[target], dur);
    applyStatus(w, target, StatusKind.STAGGERED, dur, 1);
  }

  // A hit from a burning thing lights you; the AI already models the reverse.
  if (type === DamageType.FIRE) applyStatus(w, target, StatusKind.BURNING, 2.5, 1);
  else if (type === DamageType.ACID) applyStatus(w, target, StatusKind.CORRODED, 2.5, 1);
  else if (type === DamageType.TOXIC) applyStatus(w, target, StatusKind.POISONED, 3, 1);
  else if (type === DamageType.SHOCK) applyStatus(w, target, StatusKind.SHOCKED, 1.5, 1);
  else if (type === DamageType.FROST) applyStatus(w, target, StatusKind.FROZEN, 1.5, 1);
  else if (type === DamageType.FUNGAL) applyStatus(w, target, StatusKind.INFECTED, 3, 1);

  void def;
  deps.onHit?.(target, srcIdx, amount);
}
